import { useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiClient } from '@/shared/lib/axios';
import { clinicApiPath } from '@/shared/lib/apiPath';
import { useClinicContext } from './useClinicContext';
import { useTheme } from '@/shared/providers/theme';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import type { MedicalExpensePriceIndexResponse } from '@/pages/main/medicalExpensePrices/types';

const getAll = async (
  tenant: string,
  clinicNumber: string,
  serviceType: string,
  perPage: number = 1,
  page: number = 1
): Promise<MedicalExpensePriceIndexResponse> => {
  const response = await apiClient.get<MedicalExpensePriceIndexResponse>(
    clinicApiPath(tenant, clinicNumber, '/medical-expense-prices'),
    { params: { per_page: perPage, page, service_type: serviceType } }
  );
  return response.data;
};

export const useMainMedicalExpensePrices = (perPage: number = 1, page: number = 1) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const query = useQuery<MedicalExpensePriceIndexResponse, Error>({
    queryKey: ['medical-expense-prices', 'list', tenant, clinicNumber, serviceType, perPage, page],
    queryFn: () => getAll(tenant, clinicNumber, serviceType, perPage, page),
    enabled: !!tenant && !!clinicNumber,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = query;

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};
